import {
    CollateralsAssets,
    CollateralsAssetsResponse,
    AssetOpe,
} from 'api/application/getCollateralsAssets'
import { constructVehicleDropdownValue } from 'containers/VehicleSelection/dropdowns/VehicleDropdown'
import AssetType from 'state/enums/AssetType'

function isOpeCollateral(
    collateral: CollateralsAssetsResponse,
): collateral is CollateralsAssets<AssetOpe> {
    return collateral.asset_type === AssetType.OUTDOOR_POWER_EQUIPMENT
}

export type ConvertedOpeUnit = ReturnType<typeof transformResponseCollateral>

function transformResponseCollateral(collateral: CollateralsAssets<AssetOpe>) {
    const { asset } = collateral
    const year = asset.year ? asset.year.toString() : ''
    const vehicleType = asset.equipment_type ?? ''
    const outdoorPowerEquipmentId = asset.outdoor_power_equipment_id ?? 0

    const selectedVehicleId = constructVehicleDropdownValue({
        id: outdoorPowerEquipmentId,
        vehicleType,
        uuid: '',
    })

    return {
        year,
        make: asset.make,
        model: asset.model,
        msrp: asset.msrp,
        asset_value: collateral.asset_value,
        sku: asset.sku,
        ...(asset.oem !== undefined ? { oem: asset.oem } : {}),
        modelId: '',
        selectedVehicleId,
        hasTitle: false,
        noTitleReason: '',
        noTitleOtherReason: '',
        equipmentType: vehicleType,
        equipmentSubType: asset.equipment_sub_type ?? '',
    }
}

export interface ConvertedCollateralValues {
    opeUnits: ConvertedOpeUnit[]
    primary: ConvertedOpeUnit
    secondary?: ConvertedOpeUnit
    makeId: string
    condition: 'new' | 'used'
    vehicleType: string
    equipmentType: string
}

export default function convertCollateralValues(
    collaterals: CollateralsAssetsResponse[],
    isMultiUnitEnabled = true,
): ConvertedCollateralValues | null {
    const opeCollaterals = collaterals.filter(isOpeCollateral)

    if (opeCollaterals.length < 1) {
        return null
    }

    const opeUnits = (
        isMultiUnitEnabled ? opeCollaterals : opeCollaterals.slice(0, 1)
    ).map(transformResponseCollateral)

    const rawPrimaryAsset = opeCollaterals[0].asset

    const primaryAssetCondition: 'new' | 'used' =
        rawPrimaryAsset.condition === 'N' ? 'new' : 'used'

    const primaryAssetEquipmentType = rawPrimaryAsset.equipment_type ?? ''

    const data = {
        opeUnits,
        primary: opeUnits[0],
        makeId: '',
        condition: primaryAssetCondition,
        vehicleType: primaryAssetEquipmentType,
        equipmentType: primaryAssetEquipmentType,
    }

    return opeUnits.length > 1 ? { ...data, secondary: opeUnits[1] } : data
}

/**
 * Converts fetched OPE collateral values into the legacy `vehicle.primary`
 * shape.
 *
 * When the opeUnits-based equipment selection UI isn't active (i.e.
 * `enableOpeSearchAsYouType` off), the OPE forms (`OPEApplicationForm`,
 * `CopyOpeVehicleForm`) render the legacy single-field (year/make/model) UI,
 * which only reads/writes `vehicle.primary` - never `vehicle.opeUnits`. OPE
 * multi-collateral support is exclusively provided by the opeUnits-based
 * equipment selection UI (gated behind `enableOpeMultiCollateralUi`), so the
 * legacy UI never needs a second (`vehicle.secondary`) vehicle. The first
 * `opeUnits` entry is already shaped like a legacy primary vehicle
 * (selectedVehicleId, year, make, model, etc.), so it can be used directly.
 */
export function toLegacyVehicleValues(
    collateralValues: ConvertedCollateralValues,
) {
    const { opeUnits, ...rest } = collateralValues
    const [primary] = opeUnits

    return {
        ...rest,
        primary,
    }
}
